import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  ButtonGroup,
  Grid,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ClearIcon from '@mui/icons-material/Clear';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Checkbox from '@mui/material/Checkbox';
import CheckboxGroup from './components';
import useStyles from './styles';
import styled from 'styled-components';
import { withTheme } from '@mui/styles';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      style={{
        background: '#fafafa',
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
        border: '1px solid #e0e0e0',
        marginBottom: 12,
      }}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </Typography>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function GridContainer(props) {
  const {
    handleChangeCheckbox,
    checkboxState,
    reportFilters,
    handleClearAll,
    value,
  } = props;
  return (
    <Paper elevation={0}>
      <Grid container direction="row" justify="flex-start" alignItems="center">
        <Grid item>
          <CheckboxGroup
            handleChangeCheckbox={handleChangeCheckbox}
            checkboxState={checkboxState}
            reportFilters={reportFilters}
            handleClearAll={handleClearAll}
            value={value}
            maxHeight="300px"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

GridContainer.propTypes = {
  handleChangeCheckbox: PropTypes.func,
  checkboxState: PropTypes.any.isRequired,
  reportFilters: PropTypes.any.isRequired,
  handleClearAll: PropTypes.func,
  value: PropTypes.any.isRequired,
};

function SectionsContainer(props) {
  const classes = useStyles();
  const {
    handleChangeCheckbox,
    sections,
    handleClearAll,
    tabId,
    indicatorsLoaded,
  } = props;

  if (!indicatorsLoaded && tabId === 4) {
    return (
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
        className={classes.padded}
      >
        <CircularProgress />
        &nbsp;Loading your indicators...
      </Grid>
    );
  }

  if (indicatorsLoaded && tabId === 4 && sections.length < 3) {
    return (
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
        className={classes.padded}
      >
        This user has no reports/indicators yet...
      </Grid>
    );
  }

  return (
    <Paper>
      <Grid
        container
        direction="row"
        justify="flex-start"
        alignItems="center"
        className={classes.padded}
      >
        <Grid className={classes.mainSelector}>
          <Link
            className={classes.linkSelectAllSmall}
            onClick={() => handleClearAll(true, tabId)}
          >
            Select all Sections
          </Link>
          <> - </>
          <Link
            className={classes.linkClearAllSmall}
            onClick={() => handleClearAll(false, tabId)}
          >
            Clear all Sections
          </Link>
        </Grid>
        <Grid item>
          {sections.map(section => {
            const onPressSelectAll = () => {
              handleClearAll(true, tabId, section.id);
            };

            const onPressClearAll = () => {
              handleClearAll(false, tabId, section.id);
            };
            return (
              <div key={section.id}>
                <h3
                  className={section.header ? classes.mainTitle : classes.title}
                >
                  {section.title}
                </h3>
                {((section.filters && section.filters.length > 0) ||
                  (section.stages && section.stages.length > 0)) && (
                  <Grid item xs={12}>
                    <Link
                      className={classes.linkSelectAllSmall}
                      component="button"
                      variant="body2"
                      onClick={onPressSelectAll}
                    >
                      Select All
                    </Link>
                    <> - </>
                    <Link
                      className={classes.linkClearAllSmall}
                      component="button"
                      variant="body2"
                      onClick={onPressClearAll}
                    >
                      Clear
                    </Link>
                  </Grid>
                )}
                {section.stages &&
                  section.stages.map((stage, stageIndex) => {
                    return (
                      <div key={stageIndex}>
                        <h4 className={classes.subTitle}>{stage.name}</h4>
                        <CheckboxGroup
                          handleChangeCheckbox={handleChangeCheckbox}
                          reportFilters={stage.filters}
                          section={section.id}
                          key={section.id}
                          maxHeight="auto"
                          stage={stageIndex}
                          xs={10}
                        />
                      </div>
                    );
                  })}
                {section.filters && section.filters.length > 0 && (
                  <CheckboxGroup
                    handleChangeCheckbox={handleChangeCheckbox}
                    reportFilters={section.filters}
                    section={section.id}
                    maxHeight="auto"
                    xs={10}
                  />
                )}
              </div>
            );
          })}
        </Grid>
      </Grid>
    </Paper>
  );
}

SectionsContainer.propTypes = {
  handleChangeCheckbox: PropTypes.func,
  sections: PropTypes.array,
  handleClearAll: PropTypes.func,
  tabId: PropTypes.number,
  indicatorsLoaded: PropTypes.bool,
};

export default function FilterReport(props) {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const [reportTitle, setReportTitle] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [openErrorReport, setOpenErrorReport] = React.useState(false);
  const [openDeleteReport, setOpenDeleteReport] = React.useState(false);
  const {
    checked,
    reportId,
    reportName,
    handleClearAll,
    editMode,
    handleShowNewName,
    handleOpenSnackbar,
    tabs,
    reportsData,
    indicatorsLoaded,
    includeArchived,
    onChangeArchived,
    handleRunReport,
  } = props;

  useEffect(() => {
    if (reportName !== '' && reportName !== undefined) {
      setReportTitle(reportName);
    } else {
      setReportTitle('');
    }
  }, [reportName]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeCheckbox = (
    name,
    tabId,
    sectionId,
    stageIndex,
  ) => event => {
    props.handleOnUpdateFilter(
      name,
      tabId,
      sectionId,
      stageIndex,
      event.target.checked,
    );
  };

  const handleChangeTitle = event => {
    setReportTitle(event.target.value);
  };

  const handleSaveFilters = () => {
    props.handleSaveReport(reportTitle, false);
  };

  // eslint-disable-next-line consistent-return
  const handleSave = fromSaveAndRun => {
    let atLeastOneCheched = false;
    let checkedMessage = '';
    const currentTab = value;
    if (currentTab === 3 || currentTab === 4) {
      const result = props.tabs.filter(tab => tab.id === currentTab);
      atLeastOneCheched =
        result[0].sections[0].filters.filter(
          section => section.checked === true,
        ).length > 0;
      checkedMessage =
        'You must select at least one field from the first section.';
      // atLeastOneCheched = props.tabs.filter(tab => (
      //   tab.id === currentTab &&
      //   tab.sections.filter(section => (section.filters.filter(filter => filter.checked).length > 0)))
      // ).length > 0;
    } else {
      atLeastOneCheched =
        props.tabs.filter(
          tab =>
            tab.id === currentTab &&
            tab.filters.filter(filter => filter.checked).length > 0,
        ).length > 0;
      checkedMessage = 'You must select at least one field on the report.';
    }
    if (reportTitle.length === 0) {
      setOpenErrorReport('Add a name to save the changes on the report.');
      return false;
    }
    if (!atLeastOneCheched) {
      setOpenErrorReport(checkedMessage);
      return false;
    }
    const repeatedReportTitle = reportsData.filter(
      report => report.name === reportTitle,
    );
    if (repeatedReportTitle.length > 0 && !editMode) {
      props.setOpenReportNameError(true);
      return false;
    }
    props.handleOpenSnackbar();
    props.handleSaveReport(reportTitle, fromSaveAndRun);
    if (!fromSaveAndRun) {
      props.handleShowNewName();
      setReportTitle('');
    }
  };

  const handleSaveAndRun = () => {
    handleSave(true);
    handleRunReport(true);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseErrorReportName = () => {
    setOpenErrorReport(undefined);
  };

  const handleCancel = () => {
    props.handleShowNewName();
    setOpen(false);
  };

  const handleOpenDelete = () => {
    setOpenDeleteReport(true);
  };

  const handleCloseDeleteReport = () => {
    setOpenDeleteReport(false);
  };

  const handleDelete = () => {
    props.handleDeleteReport(reportId);
    setOpenDeleteReport(false);
  };

  return (
    <div>
      <Collapse in={checked}>
        <Paper>
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
          >
            <Grid item xs={6}>
              <TextField
                className={classes.textfield}
                id="outlined-required"
                label="Enter Report Name"
                variant="outlined"
                size="small"
                onChange={handleChangeTitle}
                value={reportTitle}
              />
              <FormControlLabel
                style={{ marginTop: 7 }}
                label="Include Archived Items"
                control={
                  <Checkbox
                    checked={includeArchived}
                    onChange={onChangeArchived}
                  />
                }
              />
            </Grid>
            <Grid item>
              <ButtonGroup
                size="small"
                className={classes.buttonGroupContainer}
              >
                {editMode && (
                  <Button
                    className={classes.buttonGroup}
                    onClick={handleSaveAndRun}
                    startIcon={<SaveAltIcon />}
                  >
                    Save and Run Report
                  </Button>
                )}
                <Button
                  className={classes.buttonGroup}
                  onClick={() => handleSave(false)}
                  startIcon={<SaveIcon />}
                >
                  Save
                </Button>
                <Button
                  className={classes.buttonGroup}
                  onClick={handleClickOpen}
                  startIcon={<ClearIcon />}
                >
                  Cancel
                </Button>
                {editMode && (
                  <Button
                    className={classes.buttonGroupDelete}
                    color="primary"
                    onClick={handleOpenDelete}
                    startIcon={<HighlightOffIcon />}
                  >
                    Delete Report
                  </Button>
                )}
              </ButtonGroup>
            </Grid>
          </Grid>
          <div className={classes.root}>
            <AppBar position="static" color="default">
              <StyledTabs value={value} onChange={handleChange}>
                {tabs.map(tab => (
                  <Tab
                    label={tab.tabName}
                    {...a11yProps(tab.id)}
                    key={tab.id}
                  />
                ))}
              </StyledTabs>
            </AppBar>
            {tabs.map(tab => (
              <TabPanel value={value} index={tab.id} key={tab.id}>
                {tab.id !== 3 && tab.id !== 4 && (
                  <GridContainer
                    handleShowNewName={handleShowNewName}
                    handleOpenSnackbar={handleOpenSnackbar}
                    handleChangeCheckbox={handleChangeCheckbox}
                    handleClearAll={handleClearAll}
                    value={value}
                    reportFilters={tab.filters}
                    handleSaveFilters={handleSaveFilters}
                    reportTitle={reportTitle}
                    open={open}
                    openErrorReportName={openErrorReport}
                  />
                )}
                {(tab.id === 3 || tab.id === 4) && (
                  <SectionsContainer
                    handleChangeCheckbox={handleChangeCheckbox}
                    handleClearAll={handleClearAll}
                    sections={tab.sections}
                    tabId={tab.id}
                    indicatorsLoaded={indicatorsLoaded}
                  />
                )}
              </TabPanel>
            ))}
          </div>
        </Paper>
      </Collapse>
      <div>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <div className={classes.dialogTitle}>
              Do you want to discard the changes?
            </div>
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              No
            </Button>
            <Button onClick={handleCancel} color="primary" autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div>
        <Dialog
          open={!!openErrorReport}
          onClose={handleCloseErrorReportName}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <div className={classes.dialogTitle}>{openErrorReport}</div>
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseErrorReportName} color="primary">
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div>
        <Dialog
          open={openDeleteReport}
          onClose={handleCloseDeleteReport}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <div className={classes.dialogTitle}>
              Do you want to delete this report?
            </div>
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseDeleteReport} color="primary" autoFocus>
              No
            </Button>
            <Button onClick={handleDelete} color="primary">
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

FilterReport.propTypes = {
  checked: PropTypes.bool,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportName: PropTypes.string,
  handleClearAll: PropTypes.func,
  editMode: PropTypes.bool,
  handleShowNewName: PropTypes.func,
  handleOpenSnackbar: PropTypes.func,
  tabs: PropTypes.array,
  reportsData: PropTypes.array,
  indicatorsLoaded: PropTypes.bool,
  includeArchived: PropTypes.bool,
  onChangeArchived: PropTypes.func,
  handleRunReport: PropTypes.func,
  handleOnUpdateFilter: PropTypes.func,
  handleSaveReport: PropTypes.func,
  setOpenReportNameError: PropTypes.func,
  handleDeleteReport: PropTypes.func,
};

const StyledTabs = withTheme(styled(Tabs)`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.custom.gray};

  .Mui-selected {
    color: ${({ theme }) => theme.palette.secondary.main};
    font-weight: bold;
  }
`);
